import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { capitalize } from '@attain-apps/string-utils'
import * as rxjs from 'rxjs'

import * as state from '../../../../../../../../../state'

/**
 * Number of milliseconds to debounce rapid updates to the page content and query
 *
 * I.e. if multiple updates are made in less than this number of milliseconds,
 * only the last of those updates will actually be applied
 */
const UPDATE_DEBOUNCE_MS = 500

@ngCore.Component({
  imports: [
    ngCommon.JsonPipe,
    ds.input.text.Component,
    ds.input.code.Component,
  ],
  selector: 'atn-mosaic-dev-page-general-tab',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.mosaic.dev.pages.Collection} */
  public readonly pagesEc = ngCore.inject(state.mosaic.dev.pages.Collection)

  /**
   * Used to debounce rapid updates to avoid excessive action dispatches
   */
  private readonly _updatedQuery$ = new rxjs.BehaviorSubject<
    ents.core.mosaic.Page.Query | undefined
  >(undefined)

  /**
   * Used to debounce rapid updates to avoid excessive action dispatches
   */
  private readonly _updatedContent$ = new rxjs.BehaviorSubject<
    ents.core.mosaic.Page.Content | undefined
  >(undefined)

  constructor() {
    /** @see {@link _updatedQuery$} */
    this._updatedQuery$
      .pipe(
        rxjs.debounceTime(UPDATE_DEBOUNCE_MS),
        rxjs.map(query => {
          if (!query) {
            return
          }
          this.updatePage('query', query)
        }),
      )
      .subscribe()

    /** @see {@link _updatedContent$} */
    this._updatedContent$
      .pipe(
        rxjs.debounceTime(UPDATE_DEBOUNCE_MS),
        rxjs.map(content => {
          if (!content) {
            return
          }
          this.updatePage('content', content)
        }),
      )
      .subscribe()
  }

  /**
   * Update page property handler
   */
  public updatePage<K extends keyof ents.core.mosaic.Page.Input>(
    prop: K,
    value: ents.core.mosaic.Page.Input[K],
  ): void {
    this.pagesEc.plugins.editingPlugin.feature.actions.edit
      .from(`Dialog.${capitalize(prop)}.Inputted`)
      .dispatch({
        edit: page => {
          if (!page) {
            return
          }

          page[prop] = value
        },
      })
  }

  /**
   * Update page query handler
   */
  public updatePageQuery(query: unknown): void {
    // NOTE: we just hope this is structured properly
    this._updatedQuery$.next(query as ents.core.mosaic.Page.Query)
  }

  /**
   * Update page content handler
   */
  public updatePageContent(content: unknown): void {
    // NOTE: we just hope this is structured properly
    this._updatedContent$.next(content as ents.core.mosaic.Page.Content)
  }
}
